import { Injectable, inject } from "@angular/core";
import { catchError, map, switchMap, takeWhile, withLatestFrom } from "rxjs";

import { Actions, createEffect, ofType } from "@ngrx/effects";
// Store
import { Store } from "@ngrx/store";
import * as SportsbookActions from "../../../stores/sportsbook/sportsbook.actions";
import { SportsbookFacade } from "../../../stores/sportsbook/sportsbook.facade";
import { SportsbookState } from "../../../stores/sportsbook/sportsbook.reducers";

// Services
import { BufferFacade } from "@shared-providers/facades";
import { SportTrackPollingService } from "@sports-services/index";
import { SPORTS_NAVIGATION_SERVICE_ERROR_CODES } from "@sports-services/sports.navigation.service";
import { SportService } from "@sports-services/sports.service";

// Models
import * as models from "@sports-models/index";

// Utils
import { SportBookService } from "@sports-services/index";
import { SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION } from "@sports-services/sports.navigation.service/sports.navigations.constants";

@Injectable({ providedIn: "root" })
export class SportsbookTodayEffects {
  // Services
  actions$ = inject(Actions);
  bufferFacade = inject(BufferFacade);
  sportService = inject(SportService);

  // Utils
  sportBookService = inject(SportBookService);
  store = inject(Store<SportsbookState>);
  sportsbookFacade = inject(SportsbookFacade);
  sportTrackPollingService = inject(SportTrackPollingService);

  timer = {
    live: "live",
    regular: "regular",
    highlights: "highlights",
  };

  // TODAY PAGE
  fetchTodayCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.fetchTodayCategories),
      withLatestFrom(this.store.select("sportsbook")),
      map(([data, sportsbook]) => {
        const todayData = sportsbook["todayData"];
        const nextFiveDays = this.sportBookService.getNextFiveDays();
        return SportsbookActions.fetchTodaysDateSportLeagues({
          ...todayData,
          fiveDays: nextFiveDays,
          selectedDay: 0,
          sportsEvents: [],
        });
      }),
    ),
  );

  restoreTodayPageState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.restoreTodayPageState),
      withLatestFrom(this.store.select("sportsbook")),
      switchMap(([restoreState, sportsbook]) => {
        const newSelectedSport = restoreState?.["sportSelected"]
          ? restoreState?.["sportSelected"]
          : sportsbook.todayData?.selectedSport;
        const newSelectedDay = restoreState?.["daySelected"]
          ? restoreState["daySelected"]
          : sportsbook.todayData.selectedDay;
        return this.sportService.getCountriesByDate(newSelectedSport.CategoryId, newSelectedDay).pipe(
          map((countriesByDateData: any) => {
            const sportsEvents: models.C_TodayLeagues[] = countriesByDateData.flatMap((country) =>
              country.Leagues.map((league) => ({
                ...league,
                CountryCode: country.CountryCode,
              })),
            );
            if (!restoreState?.["selectedEventsNodeIds"]?.length) {
              return SportsbookActions.setToday({
                fiveDays: sportsbook.todayData.fiveDays,
                selectedDay: newSelectedDay,
                selectedSport: newSelectedSport,
                sportsFilters: sportsbook.todayData.sportsFilters,
                sportsEvents,
                selectedEventsNodeIds: [],
              });
            } else {
              return SportsbookActions.fetchTodayLeagueEvents({
                fiveDays: sportsbook.todayData.fiveDays,
                selectedDay: newSelectedDay,
                selectedSport: newSelectedSport,
                sportsFilters: sportsbook.todayData.sportsFilters,
                sportsEvents,
                selectedEventsNodeIds: restoreState["selectedEventsNodeIds"],
              });
            }
          }),
          catchError((e) => {
            console.log("Error in restoreTodayPageState effect");
            return [];
          }),
        );
      }),
    ),
  );

  fetchTodaysDateSportLeagues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.fetchTodaysDateSportLeagues),
      switchMap((todayPageData) => {
        this.sportTrackPollingService.stopAllPollings();

        return this.sportService
          .getCountriesByDate(todayPageData.selectedSport.CategoryId, todayPageData.selectedDay)
          .pipe(
            map((countriesByDateData: any) => {
              const sportsEvents: models.C_TodayLeagues[] = countriesByDateData.flatMap((country) =>
                country.Leagues.map((league) => ({
                  ...league,
                  CountryCode: country.CountryCode,
                })),
              );
              return SportsbookActions.setToday({
                fiveDays: todayPageData.fiveDays,
                selectedDay: todayPageData.selectedDay,
                selectedSport: todayPageData.selectedSport,
                sportsFilters: todayPageData.sportsFilters,
                sportsEvents,
                selectedEventsNodeIds: [],
              });
            }),
            catchError((e) => {
              this.sportsbookFacade.setSportsError({
                errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.GENERIC_ERROR,
                errorDescription: SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION.LOCATION_24,
              });
              return [];
            }),
          );
      }),
    ),
  );

  fetchTodayLeagueEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.fetchTodayLeagueEvents),
      withLatestFrom(this.store.select("sportsbook")),
      switchMap(([{ type, ...payload }, sportsbook]) => {
        if (!payload?.selectedEventsNodeIds.length) {
          return [];
        }
        const todayPageData = sportsbook["todayData"];
        const params = {
          parentids: payload.selectedEventsNodeIds,
          dayDifference: todayPageData.selectedDay,
          sportHandle: todayPageData.selectedSport.CategoryId,
        };
        this.sportTrackPollingService.startTodayPagePolling();
        return this.bufferFacade
          .newAuxBuffer(
            this.sportService,
            "getMultipleEventsByDateUrl",
            params,
            this.timer.regular,
            this.sportTrackPollingService.todayPolling$,
          )
          .pipe(
            takeWhile((_) => this.sportTrackPollingService.todayPolling$.getValue()),
            map(([prev, current]) => {
              const sportsEvents = this.sportBookService.setTodayLeagueEvents(
                prev,
                current,
                todayPageData.sportsEvents,
              );
              return SportsbookActions.setToday({
                fiveDays: todayPageData.fiveDays,
                selectedDay: todayPageData.selectedDay,
                selectedSport: todayPageData.selectedSport,
                sportsFilters: todayPageData.sportsFilters,
                sportsEvents,
                selectedEventsNodeIds: todayPageData.selectedEventsNodeIds,
              });
            }),
            catchError((e) => {
              this.sportsbookFacade.setSportsError({
                errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.GENERIC_POLLING_ERROR,
                errorDescription: SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION.LOCATION_25,
              });
              return [];
            }),
          );
      }),
    ),
  );

  setTodaySelectedDay$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.setTodaySelectedDay),
      withLatestFrom(this.store.select("sportsbook")),
      map(([selectedDay, sportsbook]) => {
        const todayPageData = sportsbook["todayData"];
        this.sportTrackPollingService.stopAllPollings();
        return SportsbookActions.fetchTodaysDateSportLeagues({
          fiveDays: todayPageData.fiveDays,
          selectedDay: selectedDay.selectedDay,
          selectedSport: todayPageData.selectedSport,
          sportsFilters: todayPageData.sportsFilters,
          sportsEvents: [],
          selectedEventsNodeIds: [],
        });
      }),
    ),
  );

  setTodaySelectedSport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.setTodaySelectedSport),
      withLatestFrom(this.store.select("sportsbook")),
      map(([selectedSport, sportsbook]) => {
        const todayPageData = sportsbook["todayData"];
        this.sportTrackPollingService.stopAllPollings();
        return SportsbookActions.fetchTodaysDateSportLeagues({
          fiveDays: todayPageData.fiveDays,
          selectedDay: todayPageData.selectedDay,
          selectedSport,
          sportsFilters: todayPageData.sportsFilters,
          sportsEvents: [],
          selectedEventsNodeIds: [],
        });
      }),
    ),
  );
}
